'use client';

import { Button, cn } from '@heroui/react';
import { NumberStepper } from '@heroui-pro/react/number-stepper';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import type { FC } from 'react';
import { DateTimePickerField } from '@/components/custom';
import { ColorPickerField } from '@/components/custom';
import { STROKE_STYLE_OPTIONS, type StrokeStyle } from '@/components/canvas/shared/controls/AppearanceSettings';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import type { BlockStroke, CutdownCardBlock } from '@/components/canvas/blocks/types';
import { dateValueToIsoString, isoStringToDateValue } from './dateUtils';
import { ListBox, Select } from '@heroui/react';

const DEFAULT_BG = '#ebebeb';
const DEFAULT_STROKE: BlockStroke = { color: '#6E6E6E', width: 1, style: 'solid' };
const DEFAULT_TEXT = '#2C2C2C';

function strokeLinePreview(style: StrokeStyle) {
  return (
    <span
      className="inline-block w-10 shrink-0 border-t border-current opacity-80"
      style={{
        borderTopWidth: style === 'double' ? 2 : 1,
        borderTopStyle: style,
      }}
      aria-hidden
    />
  );
}

function OptionalSectionHeader({
  title,
  enabled,
  onAdd,
  addLabel,
}: {
  title: string;
  enabled: boolean;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <p className="text-sm font-medium text-default-foreground">{title}</p>
      {!enabled ? (
        <Button
          type="button"
          variant="ghost"
          isIconOnly
          size="sm"
          aria-label={addLabel}
          onMouseDown={(e) => e.preventDefault()}
          onPress={onAdd}
        >
          <IconPlus className="h-4 w-4" stroke={1.5} />
        </Button>
      ) : null}
    </div>
  );
}

export interface CutdownCardPropertiesProps {
  block: CutdownCardBlock;
  className?: string;
}

export const CutdownCardProperties: FC<CutdownCardPropertiesProps> = ({ block, className }) => {
  const { updateBlockData } = useCanvas();
  const { data } = block;

  const patchData = (patch: Partial<CutdownCardBlock['data']>) => {
    updateBlockData(block.id, patch);
  };

  const hasBg = data.backgroundColor != null;
  const hasStroke = data.stroke != null;
  const hasNumberColor = data.numberColor != null;
  const hasLabelColor = data.labelColor != null;

  const stroke = data.stroke;
  const strokeStyle = (stroke?.style ?? 'solid') as StrokeStyle;

  return (
    <div className={cn('property-panel-view flex min-h-0 w-full shrink-0 flex-col gap-4 p-1', className)}>
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-default-foreground">截止时间</p>
        <DateTimePickerField
          granularity="minute"
          hourCycle="24-hour"
          fullWidth
          aria-label="截止时间"
          value={isoStringToDateValue(data.date)}
          onChange={(v) =>
            patchData({ date: v ? dateValueToIsoString(v) : undefined })
          }
        />
      </div>

      <div className="space-y-1.5">
        <OptionalSectionHeader
          title="卡片背景色"
          enabled={hasBg}
          onAdd={() => patchData({ backgroundColor: DEFAULT_BG })}
          addLabel="添加卡片背景色"
        />
        {hasBg ? (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ColorPickerField
                aria-label="卡片背景色"
                value={data.backgroundColor ?? DEFAULT_BG}
                onChange={(next) => patchData({ backgroundColor: next })}
                variant="secondary"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除卡片背景色"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => patchData({ backgroundColor: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <OptionalSectionHeader
          title="卡片描边"
          enabled={hasStroke}
          onAdd={() => patchData({ stroke: { ...DEFAULT_STROKE } })}
          addLabel="添加卡片描边"
        />
        {hasStroke && stroke ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <ColorPickerField
                  aria-label="描边颜色"
                  value={stroke.color}
                  onChange={(next) => patchData({ stroke: { ...stroke, color: next } })}
                  variant="secondary"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                isIconOnly
                size="sm"
                aria-label="移除卡片描边"
                onMouseDown={(e) => e.preventDefault()}
                onPress={() => patchData({ stroke: undefined })}
              >
                <IconMinus className="h-4 w-4" stroke={1.5} />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select
                aria-label="描边线型"
                variant="secondary"
                value={strokeStyle}
                onChange={(key) => {
                  if (key == null) return;
                  patchData({
                    stroke: { ...stroke, style: String(key) as BlockStroke['style'] },
                  });
                }}
              >
                <Select.Trigger>
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
                    <span className="truncate">
                      {STROKE_STYLE_OPTIONS.find((o) => o.value === strokeStyle)?.label}
                    </span>
                    {strokeLinePreview(strokeStyle)}
                  </span>
                  <Select.Indicator />
                </Select.Trigger>
                <Select.Popover>
                  <ListBox>
                    {STROKE_STYLE_OPTIONS.map((opt) => (
                      <ListBox.Item key={opt.value} id={opt.value} textValue={opt.label}>
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          {opt.label}
                          {strokeLinePreview(opt.value)}
                        </span>
                      </ListBox.Item>
                    ))}
                  </ListBox>
                </Select.Popover>
              </Select>
              <NumberStepper
                aria-label="描边线宽"
                size="sm"
                value={stroke.width}
                minValue={0}
                maxValue={24}
                step={1}
                onChange={(v) => {
                  if (v == null || Number.isNaN(v)) return;
                  patchData({ stroke: { ...stroke, width: Math.max(0, Math.round(v)) } });
                }}
              >
                <NumberStepper.Group>
                  <NumberStepper.DecrementButton />
                  <NumberStepper.Value />
                  <NumberStepper.IncrementButton />
                </NumberStepper.Group>
              </NumberStepper>
            </div>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <OptionalSectionHeader
          title="数字颜色"
          enabled={hasNumberColor}
          onAdd={() => patchData({ numberColor: DEFAULT_TEXT })}
          addLabel="添加数字颜色"
        />
        {hasNumberColor ? (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ColorPickerField
                aria-label="数字颜色"
                value={data.numberColor ?? DEFAULT_TEXT}
                onChange={(next) => patchData({ numberColor: next })}
                variant="secondary"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除数字颜色"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => patchData({ numberColor: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <OptionalSectionHeader
          title="标签颜色"
          enabled={hasLabelColor}
          onAdd={() => patchData({ labelColor: DEFAULT_TEXT })}
          addLabel="添加标签颜色"
        />
        {hasLabelColor ? (
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <ColorPickerField
                aria-label="标签颜色"
                value={data.labelColor ?? DEFAULT_TEXT}
                onChange={(next) => patchData({ labelColor: next })}
                variant="secondary"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              isIconOnly
              size="sm"
              aria-label="移除标签颜色"
              onMouseDown={(e) => e.preventDefault()}
              onPress={() => patchData({ labelColor: undefined })}
            >
              <IconMinus className="h-4 w-4" stroke={1.5} />
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
